import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

type HookReturn = [
  selectedIndex: number,
  wrapperRef: RefObject<HTMLDivElement | null>,
  inputRef: RefObject<HTMLInputElement | null>,
]
export function useInputDropdown<R = unknown>(
  searchResults: R[],
  onChange: (value?: string) => void,
  onSelect: (index: number) => void,
): HookReturn {
  // Block for handling outside click events.
  const wrapperRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!wrapperRef?.current?.contains(target)) {
        onChange(undefined)
      }
    }
    document.addEventListener('click', listener)
    return () => document.removeEventListener('click', listener)
  }, [onChange])

  // Block for handling input keyboard events.
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Enter': {
          if (selectedIndex && searchResults) {
            event.preventDefault()
            setSelectedIndex(-1)
            onSelect(selectedIndex)
          }
          break
        }
        case 'Escape': {
          event.preventDefault()
          setSelectedIndex(-1)
          onChange()
          break
        }
        case 'ArrowUp': {
          if (searchResults && selectedIndex > -1) {
            event.preventDefault()
            setSelectedIndex(selectedIndex - 1)
          }
          break
        }
        case 'ArrowDown': {
          if (searchResults && selectedIndex < searchResults.length - 1) {
            event.preventDefault()
            setSelectedIndex(selectedIndex + 1)
          }
          break
        }
      }
    },
    [onChange, onSelect, searchResults, selectedIndex],
  )
  useEffect(() => {
    const element = inputRef.current
    if (element) {
      element.addEventListener('keydown', handleKeyDown)
      return () => element.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, inputRef])

  return [selectedIndex, wrapperRef, inputRef]
}
